<template>
  <div
    v-click-outside="
      () => {
        showSearchResults = false;
        emptySearchClicked = false;
      }
    "
    class="search-container"
  >
    <input
      ref="lookup"
      :class="{ searchDisabled: !isSearchEnabled }"
      :disabled="!isSearchEnabled"
      :placeholder="menuLabel"
      class="input-field"
      type="text"
      @keyup="nextItem($event)"
      @keyup.enter="onItemSelected(searchResults[currentSearchResult])"
      @emptied="doSearch(null)"
    />
    <div v-if="!iconConfig" class="search-icon" @click="doSearch(null)">
      <font-awesome-icon icon="search" />
    </div>
    <div v-else class="search-icon" @click="doSearch(null)">
      <div v-if="showSpinner">
        <vue-simple-spinner
          v-show="showSpinner"
          class="global-search-spinner"
          size="medium"
        ></vue-simple-spinner>
      </div>
      <div v-else>
        {{ iconConfig.label }}
      </div>
    </div>
    <div v-show="showSearchResults && showResultsInDrodown" class="result-list">
      <ul>
        <li
          v-for="(item, index) in searchResults"
          :key="index"
          :class="{ 'active-item': isItemSelected(index) }"
          @click="onItemSelected(item)"
          @keyup.enter="onItemSelected(item)"
        >
          <slot :value="item">{{ item }}</slot>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import vClickOutside from 'v-click-outside';
import { searchUtils } from '@/mixins/search-utils';
import Vue from 'vue';
export default {
  name: 'TolTypeAhead',
  directives: {
    clickOutside: vClickOutside.directive
  },
  mixins: [searchUtils],
  inheritAttrs: false,
  props: {
    searchParams: {
      type: Object,
      required: true
    },
    selectedItem: {
      type: [String, Number, Boolean],
      default: ''
    },
    menuLabel: {
      type: String,
      default: ''
    },
    allowEmptySearch: {
      type: Boolean,
      default: false
    },
    storeSearch: {
      type: Boolean,
      default: false
    },
    resultsInDropdown: {
      type: Boolean,
      default: true
    },
    iconConfig: {
      type: Object,
      default: null
    },
    multiSelect: {
      type: Boolean,
      default: false
    },
    signalParams: {
      type: Object,
      default: null
    },
    noClearSearch: {
      type: Boolean,
      default: false
    },
    displayProperty: {
      type: String,
      default: null
    },
    isSearchEnabled: {
      type: Boolean,
      default: true
    },
    showSpinner: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showSearchResults: false,
      selected: this.selectedItem,
      searchResults: [],
      currentSearchResult: 0,
      emptySearchClicked: false,
      multipleSelected: [],
      searchTimer: undefined
    };
  },
  computed: {
    showResultsInDrodown() {
      return this.resultsInDropdown && this.searchResults.length > 0;
    },
    addRequiredProps() {
      return {
        'v-validate': { required: this.required },
        name: this.fieldName
      };
    }
  },
  watch: {
    /**
     * Enable deselecting of list items from action triggered outside of the component
     */
    signalParams: {
      immediate: true,
      handler(val) {
        if (val) {
          if (val.removeOne) {
            const removePos = this.multipleSelected.indexOf(val.removeOne);
            this.multipleSelected.splice(removePos, 1);
          }
          if (val.clearAll) {
            this.multipleSelected = [];
          }
        }
      }
    }
  },
  methods: {
    isItemSelected(index) {
      return this.multiSelect
        ? this.multipleSelected.includes(this.searchResults[index])
        : this.currentSearchResult == index;
    },
    toggleSearchResults() {
      this.showSearchResults = !this.showSearchResults;
    },

    onItemSelected(dropdownItem) {
      if (!this.multiSelect) {
        this.$emit('select-item', dropdownItem);
        this.selected = dropdownItem;
        this.toggleSearchResults();
      } else {
        const currentIdx = this.searchResults.indexOf(dropdownItem);
        if (!this.multipleSelected.includes(dropdownItem)) {
          this.multipleSelected.push(dropdownItem);
          this.currentSearchResult = currentIdx;
        } else {
          this.multipleSelected = this.multipleSelected.filter(item => item !== dropdownItem);
        }
        this.$emit('select-item', this.multipleSelected);
      }

      this.$refs.lookup.value = this.displayProperty ? dropdownItem[this.displayProperty] : '';
      this.emptySearchClicked = false;
    },
    nextItem(event) {
      if (event.key === 'ArrowUp' && this.currentSearchResult > 0) {
        this.currentSearchResult--;
      } else if (
        event.key === 'ArrowDown' &&
        this.currentSearchResult < this.searchResults.length - 1
      ) {
        this.currentSearchResult++;
      } else {
        const func = this.debounce(this.doSearch);
        func(event.target.value);
      }
    },
    doSearch(searchString) {
      if (searchString && searchString.trim().length === 0) {
        this.searchResults = [];
        this.$emit('search-in-progress', false);
      } else {
        this.$emit('search-in-progress', true);
      }
      const queryParams = {};
      this.searchParams.searchKeys.forEach(element => {
        queryParams[element] = searchString;
      });

      if ('objectType' in this.searchParams) {
        queryParams['object_type'] = this.searchParams['objectType'];
      }

      if ('client_id' in this.searchParams) {
        queryParams['client_id'] = this.searchParams['client_id'];
      }

      if (searchString) {
        if (this.storeSearch && (searchString.length >= 2 || searchString.length == 0)) {
          this.searchResults = this.searchParams['storeListKey']
            ? this.searchStoreList(
                this.searchParams['store'],
                searchString,
                this.searchParams['storeListKey']
              )
            : this.mapStoreObjectAndSearch(
                this.searchParams['store'],
                this.searchParams['searchKeys'],
                searchString
              );
          this.showSearchResults = this.searchResults.length > 0;
        } else if (searchString.length >= 2) {
          this.goSearchURL(queryParams, searchString);
        } else {
          this.showSearchResults = false;
        }
      } else {
        if (this.allowEmptySearch && !this.emptySearchClicked) {
          if (this.storeSearch) {
            this.searchResults = this.searchStoreList(
              this.searchParams['store'],
              searchString,
              this.searchParams['storeListKey']
            );
            this.showSearchResults = this.searchResults.length > 0;
          } else {
            this.goSearchURL(queryParams, searchString);
          }
        } else {
          this.showSearchResults = false;
          if (!this.resultsInDropdown) {
            this.$emit('filtered', {
              data: [],
              isSearchKey: this.searchParams['searchKeys'].includes('searchParam')
            });
          }
        }
        /**
         * Leave this comment unit we are sure it doesnt break anything else
         */
        //this.emptySearchClicked = !this.emptySearchClicked;
      }
    },

    goSearchURL(queryParams, searchString) {
      if (this.searchParams.urlParams) {
        queryParams = {
          ...queryParams,
          ...this.searchParams.urlParams
        };
      }

      Vue.axios
        .get(this.searchParams.url, {
          params: {
            ...queryParams
          }
        })
        .then(res => {
          this.searchResults = res.data;
          this.showSearchResults = this.searchResults.length > 0;
          this.$emit('search-in-progress', false);
          if (!this.resultsInDropdown) {
            this.$emit('filtered', {
              data: res.data,
              isSearchKey: true,
              searchParam: searchString
            });
          }
        });
    },
    debounce(func, timeout = 300) {
      return (...args) => {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => {
          func.apply(this, args);
        }, timeout);
      };
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../styles/colors.scss';

.searchDisabled {
  background-color: $light-grey-2;
}
.search-container {
  display: flex;
  flex-direction: column;
  position: relative;
  align-items: center;
  border: 1px solid $input-border;
  background-color: $white;
  height: 45px;

  @media only screen and (min-width: 768px) {
    flex-direction: row;
  }

  &.bordered {
    height: 40px;
    background-color: $white;
    border: 1px solid $blue;

    .search-icon {
      display: flex;
      width: 40px;
      justify-content: center;
      height: 100%;
      align-items: center;
      color: $white;
      background-color: $blue;
    }
    .icon-wrapper {
      border-right: 0;
      border-left: 1px solid $blue;
    }
  }

  .search-input {
    display: flex;
    justify-content: space-between;
    padding-top: 5px;
    padding-bottom: 3px;
    border-bottom: 1px solid $row-border;
    width: 100%;
  }

  .search-icon {
    display: flex;
    background-color: $light-grey-2;
    color: $dark-blue;
    padding: 8px;
    height: 100%;
    align-items: center;
    justify-content: center;
    min-width: 80px;
    &:hover {
      cursor: pointer;
    }
    position: relative;
    svg {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }

  .input-field {
    display: flex;
    flex-grow: 1;
    height: 100%;
    font-size: 0.9em;
    border: none;
    padding: 8px 0;
    &:focus {
      outline: none;
    }
  }

  &.no-box {
    .search-reveal {
      height: auto;
      padding: 0;
      border: 0;
    }
  }

  .result-list {
    position: absolute;
    background-color: $white;
    opacity: 1;
    width: 100%;
    z-index: 50;
    top: 105%;
    max-height: 450px;
    overflow-y: scroll;

    > ul {
      list-style: none;
      margin: 0;
      padding: 0;
      > li {
        padding: 15px 15px;
        border-top: 1px solid $row-border;
        &.active-item {
          background-color: $light-blue;
          color: $dark-blue;
        }

        &:hover {
          cursor: pointer;
        }

        > span {
          margin-right: 15px;
        }
      }
    }
  }

  &.blue {
    .result-list {
      ul > li {
        background-color: $light-blue;
        &.active-item {
          background-color: $dark-blue;
          color: $mid-blue;
        }
      }
    }
  }

  &.horizontal {
    display: inline-flex;
    width: 25%;
  }

  &.top {
    .result-list {
      top: -100%;
    }
  }

  &.right {
    .result-list {
      left: 100%;
      transform: translate(0, -50%);
    }
  }
  &.left {
    .result-list {
      left: -100%;
      transform: translate(0, -50%);
    }
  }

  .field-label {
    background-color: $light-grey-1;
    color: $white;
    text-align: center;
  }
  &.error {
    border-bottom: 1px solid $red;
    background-color: $modified-form;
  }
}
</style>
